import type { BookingView, ContractOverview, CurrentSession } from '@elite-residence/api-client';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { STATUS_LABELS } from '../booking/BookingPages';
import { loadBookings } from '../booking/loadBookings';
import { businessDate } from '../dates';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { Chip, type ChipTone, PageHead } from '../ui/design';
import { Loading, QueryFailure, readResponse } from '../ui/ui';

// Contracts overview for management (approved prototype "Contracts"). Read
// only: each contract is worked on inside its booking. Built from existing
// APIs: the contract funnel report (report.operational) and, for the rows on
// the current page only, each booking's contract (contract.view) and basic
// customer name (customer.basic_view). There is no contract listing API, so
// the state filter is the booking status.

type Booking = BookingView['booking'];
type Version = ContractOverview['versions'][number];

const PAGE = 15;

const text: Dictionary<
  | 'title'
  | 'sub'
  | 'branch'
  | 'last30'
  | 'funnel'
  | 'bookings'
  | 'versions'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'pdfFailed'
  | 'invalidated'
  | 'live'
  | 'reserved'
  | 'active'
  | 'past'
  | 'all'
  | 'booking'
  | 'customer'
  | 'stay'
  | 'contract'
  | 'notIssued'
  | 'issuedNotSent'
  | 'pdfReady'
  | 'pdfProcessing'
  | 'version'
  | 'previous'
  | 'next'
  | 'pageOf'
  | 'none'
  | 'failed'
  | 'loading'
> = {
  en: {
    title: 'Contracts',
    sub: 'Overview for management. Each contract is worked on inside its booking. A signed contract is a fixed record.',
    branch: 'Branch',
    last30: 'Versions issued in the last 30 days',
    funnel: 'Contract funnel',
    bookings: 'Bookings',
    versions: 'Versions issued',
    sent: 'Sent',
    viewed: 'Viewed',
    signed: 'Signed',
    pdfFailed: 'Signed, PDF failed',
    invalidated: 'Invalidated',
    live: 'Live',
    reserved: 'Reserved',
    active: 'Active',
    past: 'Past',
    all: 'All',
    booking: 'Booking',
    customer: 'Customer',
    stay: 'Stay',
    contract: 'Contract',
    notIssued: 'Not issued',
    issuedNotSent: 'Issued, not sent',
    pdfReady: 'PDF ready',
    pdfProcessing: 'PDF processing',
    version: 'Version',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'of',
    none: 'No bookings match.',
    failed: 'The contract could not be loaded.',
    loading: 'Loading…',
  },
  ar: {
    title: 'العقود',
    sub: 'نظرة عامة للإدارة. يُعمل على كل عقد من داخل حجزه. العقد الموقّع سجل ثابت.',
    branch: 'الفرع',
    last30: 'النسخ الصادرة في آخر 30 يوماً',
    funnel: 'مسار العقود',
    bookings: 'الحجوزات',
    versions: 'النسخ الصادرة',
    sent: 'مُرسلة',
    viewed: 'تمت مشاهدتها',
    signed: 'موقّعة',
    pdfFailed: 'موقّعة، تعذر ملف PDF',
    invalidated: 'ملغاة',
    live: 'جارية',
    reserved: 'محجوزة',
    active: 'نشطة',
    past: 'سابقة',
    all: 'الكل',
    booking: 'الحجز',
    customer: 'العميل',
    stay: 'الإقامة',
    contract: 'العقد',
    notIssued: 'لم يُصدر',
    issuedNotSent: 'صدر ولم يُرسل',
    pdfReady: 'ملف PDF جاهز',
    pdfProcessing: 'جارٍ إعداد PDF',
    version: 'النسخة',
    previous: 'السابق',
    next: 'التالي',
    pageOf: 'من',
    none: 'لا توجد حجوزات مطابقة.',
    failed: 'تعذر تحميل العقد.',
    loading: 'جارٍ التحميل…',
  },
};

type Filter = 'live' | 'reserved' | 'active' | 'past' | 'all';
const FILTERS: Record<Filter, (b: Booking) => boolean> = {
  live: (b) => b.status === 'RESERVED' || b.status === 'ACTIVE',
  reserved: (b) => b.status === 'RESERVED',
  active: (b) => b.status === 'ACTIVE',
  past: (b) => ['COMPLETED', 'ENDED_EARLY', 'CANCELLED', 'NO_SHOW'].includes(b.status),
  all: () => true,
};

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

const day = (instant: string | null | undefined) => (instant ? businessDate(new Date(instant)) : '–');

function ContractState({ version }: { readonly version: Version | undefined }) {
  const t = useText(text);
  let tone: ChipTone = 'neutral';
  let label = t.notIssued;
  if (version?.signedAt) {
    tone = 'success';
    label = `${t.signed} · ${version.pdf ? t.pdfReady : version.pdfFailed ? t.pdfFailed : t.pdfProcessing}`;
  } else if (version?.invalidationReason) {
    tone = 'danger';
    label = t.invalidated;
  } else if (version?.viewedAt) {
    tone = 'info';
    label = t.viewed;
  } else if (version?.sentAt) {
    tone = 'info';
    label = t.sent;
  } else if (version) {
    tone = 'warning';
    label = t.issuedNotSent;
  }
  return <Chip tone={tone}>{label}</Chip>;
}

function Funnel({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const [branch, setBranch] = useState('');
  const allowed = session.permissions.includes('report.operational');
  const branches = useQuery({
    queryKey: ['contracts', 'branches'],
    enabled: allowed,
    queryFn: async ({ signal }) =>
      readResponse(await client.listBranches({}, { signal })).branches.filter((b) => !b.archived),
  });
  const branchId = branches.data?.find((b) => b.id === branch)?.id ?? branches.data?.[0]?.id;
  const today = businessDate(new Date());
  const funnel = useQuery({
    queryKey: ['contracts', 'funnel', branchId, today],
    enabled: Boolean(branchId),
    queryFn: async ({ signal }) =>
      branchId
        ? readResponse(
            await client.contractFunnel(
              { query: { branchId, asOf: today, from: addDays(today, -29), to: addDays(today, 1) } },
              { signal },
            ),
          )
        : null,
  });
  if (!allowed) return null;
  const card = (label: string, value: number | null | undefined) => (
    <div className="er-stat">
      <span className="er-stat__label">{label}</span>
      <span className="er-stat__value">{value ?? '–'}</span>
    </div>
  );
  return (
    <section className="er-panel er-section-block" aria-label={t.funnel}>
      <div className="er-panel__head">
        <h2 className="er-panel__title">{t.funnel}</h2>
        <span className="er-cell-sub">{t.last30}</span>
        <span className="er-spacer" />
        <select
          aria-label={t.branch}
          value={branchId ?? ''}
          style={{ width: 240 }}
          onChange={(e) => setBranch(e.target.value)}
        >
          {(branches.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
      <div className="er-panel__body">
        <QueryFailure query={funnel} />
        {funnel.isPending && branchId ? <Loading /> : null}
        {funnel.data ? (
          <div className="er-money">
            {card(t.bookings, funnel.data.bookings)}
            {card(t.versions, funnel.data.versions)}
            {card(t.sent, funnel.data.sentVersions)}
            {card(t.viewed, funnel.data.viewedVersions)}
            {card(t.signed, funnel.data.signedVersions)}
            {card(t.pdfFailed, funnel.data.signedWithPdfFailure)}
            {card(t.invalidated, funnel.data.invalidatedVersions)}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function ContractsOverviewPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const [filter, setFilter] = useState<Filter>('live');
  const [page, setPage] = useState(0);
  const allowed = session.permissions.includes('contract.view') && session.permissions.includes('booking.view');
  const bookings = useQuery({
    queryKey: ['contracts', 'bookings'],
    enabled: allowed,
    queryFn: async ({ signal }) => loadBookings(client, {}, signal),
  });
  const all = [...(bookings.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const filtered = all.filter(FILTERS[filter]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const current = Math.min(page, pages - 1);
  const rows = filtered.slice(current * PAGE, current * PAGE + PAGE);
  const contracts = useQueries({
    queries: rows.map((b) => ({
      queryKey: ['contract', b.id],
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const r = await client.getBookingContract({ path: { bookingId: b.id } }, { signal });
        if (r.status !== 200) throw new Error('contract_unavailable');
        return r.body;
      },
    })),
  });
  const customerIds = [...new Set(rows.map((b) => b.customerId))];
  const customers = useQueries({
    queries: session.permissions.includes('customer.basic_view')
      ? customerIds.map((customerId) => ({
          queryKey: ['board', 'customer', customerId],
          staleTime: 60_000,
          queryFn: async ({ signal }: { signal: AbortSignal }) =>
            readResponse(await client.getCustomer({ path: { customerId } }, { signal })).customer,
        }))
      : [],
  });
  const name = new Map(customers.flatMap((q) => (q.data ? [[q.data.id, q.data.name] as const] : [])));
  if (!allowed) return null;
  return (
    <main>
      <PageHead title={t.title} sub={t.sub} />
      <Funnel session={session} />
      <div className="er-tabs" role="tablist" aria-label={t.title}>
        {(Object.keys(FILTERS) as Filter[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="er-tab"
            aria-selected={filter === key}
            onClick={() => {
              setFilter(key);
              setPage(0);
            }}
          >
            {t[key]}
            {bookings.data ? <span className="er-tab__count">{all.filter(FILTERS[key]).length}</span> : null}
          </button>
        ))}
      </div>
      {bookings.isPending ? (
        <Loading />
      ) : bookings.isError ? (
        <QueryFailure query={bookings} />
      ) : (
        <div className="er-tablewrap">
          <table aria-label={t.title}>
            <thead>
              <tr>
                <th>{t.booking}</th>
                <th>{t.customer}</th>
                <th>{t.stay}</th>
                <th>{t.contract}</th>
                <th>{t.sent}</th>
                <th>{t.viewed}</th>
                <th>{t.signed}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="er-empty">{t.none}</div>
                  </td>
                </tr>
              ) : (
                rows.map((b, index) => {
                  const contract = contracts[index];
                  const version = contract?.data?.versions.at(-1);
                  return (
                    <tr key={b.id}>
                      <td>
                        <Link className="er-mono" to={`/bookings/${b.id}?tab=contract`}>
                          {b.displayReference}
                        </Link>
                        <div className="er-cell-sub">{STATUS_LABELS[b.status]?.[locale] ?? b.status}</div>
                      </td>
                      <td>{name.get(b.customerId) ?? '–'}</td>
                      <td className="er-mono">
                        {b.start} → {b.end}
                      </td>
                      <td>
                        {contract?.isPending ? (
                          <span className="er-cell-sub">{t.loading}</span>
                        ) : contract?.isError ? (
                          <span className="er-cell-sub">{t.failed}</span>
                        ) : (
                          <>
                            <ContractState version={version} />
                            {version ? (
                              <div className="er-cell-sub">
                                {t.version} {version.versionNumber}
                              </div>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="er-mono">{day(version?.sentAt)}</td>
                      <td className="er-mono">{day(version?.viewedAt)}</td>
                      <td className="er-mono">{day(version?.signedAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {pages > 1 ? (
            <div className="er-panel__foot er-actionbar">
              <button
                className="er-btn er-btn--secondary er-btn--sm"
                type="button"
                disabled={current === 0}
                onClick={() => setPage(current - 1)}
              >
                {t.previous}
              </button>
              <span className="er-cell-sub">
                {current + 1} {t.pageOf} {pages}
              </span>
              <button
                className="er-btn er-btn--secondary er-btn--sm"
                type="button"
                disabled={current >= pages - 1}
                onClick={() => setPage(current + 1)}
              >
                {t.next}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </main>
  );
}
